import { TetrisSpinner } from '@/components/ui/tetris-spinner';
import { useOrganizationList, useUser } from '@clerk/clerk-react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ContextualAlert } from '@/components/contextual-alert';
import {
  generateAccessRequestEmail,
  generateEmailLink,
  generateSupportEmail
} from '@/utils/email-templates';
import { Logo } from '@/components/logos/logo';

export const RequestAccessPage = () => {
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const { user } = useUser();
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: {
      infinite: true
    }
  });
  const [searchParams] = useSearchParams();
  const isOrgNotFound = searchParams.get('error') === 'organization_not_found';

  const handleRequestAccess = () => {
    const userInfo = {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.primaryEmailAddress?.emailAddress
    };

    const emailTemplate = isOrgNotFound
      ? generateSupportEmail(slug, userInfo)
      : generateAccessRequestEmail(slug, userInfo);

    window.location.href = generateEmailLink(emailTemplate);
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
        <TetrisSpinner width={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-xl shadow-lg">
        <CardHeader className="text-center space-y-6 pb-4">
          <div className="mx-auto">
            <Logo icon="contextual" width={36} height={36} />
          </div>
          <ContextualAlert
            message={
              isOrgNotFound
                ? "This store hasn't been set up on Contextual yet. Please contact support if you believe this is an error."
                : "You do not have access to this store. Please request access from the store's admin or email via the button below."
            }
          />
          <div className="flex justify-center">
            <Button onClick={handleRequestAccess} size="sm">
              {isOrgNotFound ? 'Contact Support' : 'Request Access via Email'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-semibold mb-6">Your Available Stores</h2>
              <div className="space-y-4">
                {userMemberships.data.map((mem) => (
                  <div
                    key={mem.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={mem.organization.imageUrl}
                        alt={mem.organization.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{mem.organization.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {mem.organization.slug}.myshopify.com
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setActive({ organization: mem.organization.id })
                          .then(() => {
                            void navigate(`/merchants/${mem.organization.slug!}/dashboard`);
                          })
                          .catch((e: unknown) => {
                            console.error('set active org', e);
                          });
                      }}
                    >
                      Access Store
                    </Button>
                  </div>
                ))}
                {userMemberships.data.length === 0 && (
                  <p className="text-muted-foreground">You do not have access to any stores yet.</p>
                )}
              </div>
            </div>

            {userMemberships.hasNextPage && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  userMemberships.fetchNext();
                }}
              >
                Load more stores
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
